import { Button, DatePicker, DatePickerInput } from '@carbon/react';
import { DateFilterProps } from '../types';
import styles from './date-filter.module.scss';

/**
 * Convert Date object to yyyy-MM-dd string format for storage.
 * @param {Date | null} date - The date to format
 * @return {string | null} Formatted date string or null
 */
function formatDate(date: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse yyyy-MM-dd string into Date object.
 * @param {string | null} dateStr - The date string to parse
 * @return {Date | undefined} Parsed Date or undefined
 */
function parseDate(dateStr: string | null): Date | undefined {
  if (!dateStr) return undefined;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Date range filter component using Carbon DatePicker.
 * Allows selecting start and end dates for filtering.
 * @param {DateFilterProps} props - Component props
 * @param {string} props.id - Unique identifier for the filter
 * @param {string} props.label - Display label for the filter
 * @param {Array} props.value - Current date range [start, end] as ISO strings
 * @param {Function} props.onChange - Callback when date range changes
 * @return {JSX.Element} The date filter element
 */
export function DateFilter({
  id,
  label,
  value,
  onChange,
  autoComplete,
}: DateFilterProps) {
  // Carbon's DatePickerInputProps extends HTMLAttributes<HTMLInputElement>
  // (not InputHTMLAttributes), so `autoComplete` isn't on its TS surface even
  // though it's forwarded to the underlying <input>. Cast to satisfy TS.
  //
  // Unset emits no attribute at all rather than defaulting to 'on': this filter
  // is shared by ~22 tables that predate the prop, and none of them opted into
  // a change in the browser's autofill behavior.
  const autoCompleteProps =
    autoComplete === undefined
      ? {}
      : ({ autoComplete: autoComplete ? 'on' : 'off' } as Pick<
          React.InputHTMLAttributes<HTMLInputElement>,
          'autoComplete'
        >);
  const [startDate, endDate] = value;
  const hasValue = !!startDate || !!endDate;

  const handleChange = (dates: Date[]) => {
    const [start, end] = dates;
    onChange([formatDate(start) ?? null, formatDate(end) ?? null]);
  };

  /**
   * Clears both start and end dates.
   */
  const handleClear = () => {
    onChange([null, null]);
  };

  // Build initial value array for DatePicker
  const dateValue: Date[] = [];
  const startParsed = parseDate(startDate);
  const endParsed = parseDate(endDate);
  if (startParsed) dateValue.push(startParsed);
  if (endParsed) dateValue.push(endParsed);

  return (
    <div className={styles.dateFilter}>
      {label && <span className={styles.dateLabel}>{label}</span>}
      <div className={styles.dateInputs}>
        <DatePicker
          datePickerType="range"
          dateFormat="m/d/Y"
          onChange={handleChange}
          value={dateValue}
        >
          <DatePickerInput
            id={`${id}-start`}
            labelText=""
            hideLabel
            placeholder="mm/dd/yyyy"
            size="md"
            {...autoCompleteProps}
          />
          <DatePickerInput
            id={`${id}-end`}
            labelText=""
            hideLabel
            placeholder="mm/dd/yyyy"
            size="md"
            {...autoCompleteProps}
          />
        </DatePicker>
      </div>
      <Button
        kind="tertiary"
        size="sm"
        className={styles.clearButton}
        onClick={handleClear}
        disabled={!hasValue}
      >
        Clear
      </Button>
    </div>
  );
}
